import React, { useEffect, useState } from "react";
import { EditOutlined, CopyOutlined, DeleteOutlined, PhoneOutlined, CloseOutlined } from '@ant-design/icons';
import { BASE_URL, DateFormat, DateTimeFormat } from '../Components/Constant';
import { Table, Typography, Tooltip, Popconfirm, Checkbox, Spin, Avatar, Tag, Input, InputNumber, Space, Button, message, Affix, Row, Image } from 'antd';
import { getUniqueColor, convertDateToUserTimezone } from '../Components/Utility';
import ApiService from '../Components/apiService'; // Import ApiService class
import { useSelector } from "react-redux";
import Cookies from 'js-cookie';

import { LookupCell } from './LookupCell';

import dayjs from 'dayjs';

const { Title } = Typography;

const ListViewTable = ({
    fieldsData,
    fetchCurrency,
    getConvertedValue,
    // activeCurrencies,
    records,
    objectName,
    fetchRecords,
    handleLabelClick,
    handleCloneClick,
    handleEditClick,
    deleteRecord,
    totalRecords,
    pageSize,
    setPageSize,
    selectedListView,
    currentPage,
    keyword,
    objName,
    isGlobalSearch = false,
    fetchSingleObjectRecords,
    setCurrentPage,
    setLoading,
    loading

}) => {
    const [columns, setColumns] = useState([]);
    const [scrollWidth, setScrollWidth] = useState('100vw'); // Add scroll width state
    const [editingKey, setEditingKey] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [inlineEditing, setInlineEditing] = useState(false);
    const [editedRecords, setEditedRecords] = useState([]);
    const [editRecordId, setEditRecordId] = useState('');
    const [highlightedFields, setHighlightedFields] = useState([]); // To track unsaved edits
    const security = useSelector(state => state.data.items[0]);
    const [currency, setCurrency] = useState('');
    const defaultCurrency = useSelector(state => state.data.orgItem);

  
    const fetchOrgSetupDetails = async () => {
        setCurrency(defaultCurrency?.currency);
    };

    useEffect(() => {
        fetchOrgSetupDetails();
    }, []);




    const isEditing = (recordKey, fieldName) =>
        editingKey === recordKey && editingField === fieldName;

    const handleInlineEditClick = (recordKey, fieldName, recordId) => {
        if (security?.object_settings?.[objectName]?.object_setting.edit === false) {
            message.error('You dont have permission to edit ');
            return;
        }
        setInlineEditing(true);
        setEditingKey(recordKey);
        setEditingField(fieldName);
        setEditRecordId(recordId);
    };

    const saveEdit = (record, fieldName, value) => {
        // Update the specific field in the editedRecords state
        const updatedRecord = record;
        updatedRecord[fieldName] = value;
        setEditedRecords((prevRecords) => {
            // Check if the record is already in the setEditedRecords array
            const existingRecordIndex = prevRecords.findIndex(
                (r) => r._id === updatedRecord._id // Assuming 'id' is the unique identifier
            );

            if (existingRecordIndex !== -1) {
                // If the record exists, update it
                const newRecords = [...prevRecords];
                newRecords[existingRecordIndex] = updatedRecord;
                return newRecords;
            } else {
                // If it doesn't exist, add it
                return [...prevRecords, updatedRecord];
            }
        });



        setHighlightedFields((prevFields) => [
            ...prevFields.filter((f) => !(f.recordId === record._id && f.fieldName === fieldName)),
            { recordId: record._id, fieldName },
        ]);

    };

    const handleSave = async () => {
        const body = {
            "object_name": objectName,
            "updates": editedRecords
        }

        try {
            const apiService = new ApiService(`${BASE_URL}/bulk_update_records`, {
                'Content-Type': 'application/json',
            }, 'PUT', body);
            const res = await apiService.makeCall();
            message.success('Records updated successfully');
            setInlineEditing(false);
            fetchRecords(selectedListView, currentPage);
            fetchColumns();
            setEditingKey(null);
            setEditingField(null);
            setEditRecordId('');
            setEditedRecords([]);
            setHighlightedFields([]);
        } catch (err) {

            const errorMessage = err && typeof err === 'object'
                ? Object.entries(err).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
                : 'Failed to fetch records due to an unknown error';
            message.error(errorMessage);

        }
    };

    const handleCancel = () => {
        setInlineEditing(false);
        fetchRecords(selectedListView, currentPage);
        setEditingKey(null);
        setEditingField(null);
        setEditRecordId('');
        setEditedRecords([]);
        setHighlightedFields([]); // Clear highlights
        fetchColumns();

    };

    useEffect(() => {
        fetchColumns();
    }, [fieldsData, records, inlineEditing, editingField, editRecordId]);

    useEffect(() => {
        fetchCurrency();  // Fetch mt_currencies
    }, []);

    const fetchColumns = () => {
        const filteredFieldsData = (fieldsData).filter(
            field => !['recordCount', 'CreatedBy', 'LastModifiedBy'].includes(field.name)
        );

        const nameField = filteredFieldsData.find(field => field.name === 'Name');

        const otherFields = filteredFieldsData
            .filter(field => field.name !== 'Name' && !field.is_auto_number)
        const fieldsToShow = [nameField, ...otherFields].filter(Boolean);


        const getCellHighlightStyle = (fieldName, recordId) => {
            return highlightedFields.some(
                (highlightedField) =>
                    highlightedField.fieldName === fieldName &&
                    highlightedField.recordId === recordId
            )
                ? { backgroundColor: '#fffbcc' } // Highlight color
                : {};
        };

        const columns = fieldsToShow.map((field, index) => ({
            title: field.label,
            dataIndex: field.name,
            key: field.name,
            width: field.name === 'Name' ? 180 :
                field.type === 'address' ? 220 :
                    field.type === 'multiselect' ? 180 :
                        field.type === 'richtext' ? 220 :
                            field.type === 'textarea' ? 180 :
                                field.type === 'lookup' ? 160 :
                                    field.type === 'email' ? 180 :
                                        field.type === 'url' ? 180 :
                                            field.type === 'image' ? 90 :
                                                field.type === 'boolean' ? 90 :
                                                    field.type === 'date' ? 120 :
                                                        field.type === 'datetime' ? 160 :
                                                            field.type === 'currency' ? 130 :
                                                                field.type === 'decimal' ? 120 :
                                                                    field.type === 'percentage' ? 110 :
                                                                        field.type === 'integer' ? 110 :
                                                                            field.type === 'phone' ? 140 :
                                                                                140, // Default width for other fields
            minWidth: 100, // Minimum width to prevent excessive shrinking
            sorter: field.name === 'Name' ? (a, b) => (a[field.name]?.localeCompare(b[field.name]) || 0) :
                field.type === 'integer' ? (a, b) => a.Num - b.Num : undefined,
            ellipsis: {
                showTitle: false, // Use custom tooltip instead of default
            },
            onCell: (record) => ({
                style: getCellHighlightStyle(field.name, record._id),
            }),
            render: (text, record) => {
                if (index === 0) {
                    if (inlineEditing && record._id === editRecordId && field.name === editingField) {
                        return (
                            <Input
                                defaultValue={text}
                                size="small"
                                onChange={(e) =>
                                    saveEdit(record, field.name, e.target.value)
                                }
                            />
                        );
                    } else {
                        return (
                            <div
                                style={{ position: "relative", display: "flex", alignItems: "center" }}
                                onMouseEnter={(e) => {
                                    const editIcon = e.currentTarget?.querySelector?.(".edit-icon");
                                    if (editIcon) {
                                        editIcon.style.visibility = "visible";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    const editIcon = e.currentTarget?.querySelector?.(".edit-icon");
                                    if (editIcon) {
                                        editIcon.style.visibility = "hidden";
                                    }
                                }}
                            >
<a
                                    href={`/record/${objectName}/${record._id}`}
                                    target="_self"
                                >
                                    {text}
                                </a>                                                                {["string", "integer", "boolean"].includes(field.type) &&
                                    !field.is_auto_number &&
                                    !field.formula && (
                                        (console.log('dfgbnfh')),
                                        <Tooltip title="Edit">
                                            <EditOutlined
                                                className="edit-icon"
                                                style={{
                                                    marginLeft: 6,
                                                    visibility: "hidden",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handleInlineEditClick(record.key, field.name, record._id)}
                                            />
                                        </Tooltip>

                                    )}
                            </div>
                        );
                    }
                }

                if (inlineEditing && (field.type === 'string' || field.type === 'integer' || field.type === 'boolean')) {

                    const isDisable = !security?.object_settings?.[objectName]?.field_setting?.[field.name]?.edit;


                    if (field.type === 'boolean' && record._id === editRecordId && field.name === editingField) {
                        return <Checkbox
                            checked={text}
                            disabled={isDisable}
                            onChange={(e) =>
                                saveEdit(record, field.name, e.target.checked)
                            }
                        />;
                    }
                    else if (field.type === 'string' && record._id === editRecordId && field.name === editingField && !field.is_auto_number) {

                        return (
                            <Input
                                defaultValue={text}
                                size="small"
                                disabled={isDisable}
                                onChange={(e) =>
                                    saveEdit(record, field.name, e.target.value)
                                }

                            />
                        );
                    }
                    else if (field.type === 'integer' && record._id === editRecordId && field.name === editingField) {
                        return (
                            <InputNumber
                                defaultValue={text}
                                size="small"
                                disabled={isDisable}
                                onChange={(e) =>
                                    saveEdit(record, field.name, e)
                                }

                            />
                        );
                    }

                }
                else {
                    if (field.type === 'date') {
                        const converteddate = convertDateToUserTimezone(text, DateFormat);

                        return text ? converteddate : ''; // Format date as DD-MM-YYYY
                    }
                    else if (field.type === 'image') {
                        return text ? (
                            <Image
                                src={text}
                                width={60}
                                style={{ height: 60 || 'auto', borderRadius: '6px' }}
                                alt="image"
                            />
                        ) : (
                            ''
                        )
                    }
                    else if (field.type === 'datetime') {
                        const converteddate = convertDateToUserTimezone(text, DateTimeFormat);

                        return text ? converteddate : ''; // Format DateTime as DD/MM/YYYY HH:mm:ss
                    }
                    else if (field.type === 'currency') {
                        const value = parseFloat(text);
                        const recordCurrency = record.CurrencyIsoCode; // e.g., 'CAD'
                        const userCurrency = Cookies.get('userCurrency'); // fallback to login user currency if needed

                        if (isNaN(value)) return ''; // Empty if not a number

                        const convertedValue = getConvertedValue(value, recordCurrency, userCurrency);

                        return (
                            <span>
                                {recordCurrency} {value.toFixed(2)}
                                {recordCurrency !== userCurrency && (
                                    <span style={{ color: '#888', marginLeft: 6 }}>
                                        ({userCurrency} {convertedValue})
                                    </span>
                                )}
                            </span>
                        );
                    }
                    else if (field.type === 'percentage') {
                        return text ? `${text}%` : ''; // Format as currency with dollar sign
                    }
                    else if (field.type === 'decimal') {
                        return text === undefined || text === null || text === '0' ? '' : Number(text).toFixed(2); // Show 0.00 for blank values
                    } else if (field.type === 'email') {
                        return text ? (
                            <a href={`mailto:${text}`} target="_blank" rel="noopener noreferrer">
                                {text}
                            </a>
                        ) : '';
                    } else if (field.type === 'url') {
                        return text ? (
                            <a href={text.startsWith('http') ? text : `http://${text}`} target="_blank" rel="noopener noreferrer">
                                {text}
                            </a>
                        ) : '';
                    } else if (field.type === 'lookup') {

                        let lookupId = '';

                        lookupId = record[field.name];
                        const ob = field.parent_object_name;
                          


                        if (!isGlobalSearch && !record[field.name]?._id) {
                            return '';
                        }
                        else {
                               return <LookupCell lookupId={lookupId} field={field} ob={ob} />;


                        }
                    }
                    else if (field?.type === 'address') {
                        if (typeof text === 'string') {
                            return text;
                        }
                        if (text && typeof text === 'object') {
                            const { street, city, state, country, postal_code } = text;
                            return [street, city, state, country, postal_code].filter(Boolean).join(', ');
                        }
                        return '';
                    }
                    else if (field.type === 'Phone' && text) {
                        return (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <PhoneOutlined style={{ marginRight: 6 }} />

                                {text || ''}
                            </div>
                        );
                    }
                    else if (field.type === 'richtext') {
                        return (
                            <div dangerouslySetInnerHTML={{ __html: text }} />
                        );
                    }
                    else if (field.type === 'multiselect') {
                        return Array.isArray(text) ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {text.map((value, index) => (
                                    <Tag
                                        key={index}
                                        color={getUniqueColor(index)}
                                        style={{
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '6px',
                                            lineHeight: '1.4',
                                        }}
                                    >
                                        {value}
                                    </Tag>
                                ))}
                            </div>
                        ) : (
                            text || ''
                        );
                    } else if (field.type === 'formula' || field.is_formula) {
                        const value = text; // Use text directly, as AntD passes the value as text

                        if (value && typeof value === 'object' && !Array.isArray(value)) {
                            const addressKeys = ['street', 'city', 'state', 'country', 'postal_code', 'pincode'];
                            const addressParts = addressKeys.map(k => value[k]).filter(Boolean);
                            if (addressParts.length > 0) {
                                return addressParts.join(', ');
                            }
                            return JSON.stringify(value);
                        }

                        if (Array.isArray(value)) {
                            return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {value.map((v, idx) => (
                                        <Tag
                                            key={idx}
                                            color={getUniqueColor(idx)}
                                            style={{
                                                fontSize: '12px',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                lineHeight: '1.4',
                                            }}
                                        >
                                            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                        </Tag>
                                    ))}
                                </div>
                            );
                        }

                        if (typeof value === 'boolean') {
                            return <Checkbox checked={value} disabled />;
                        }

                        if (typeof value === 'number') {
                            return value;
                        }

                        if (typeof value === 'string') {
                            if (value.includes('@') && value.includes('.')) {
                                return (
                                    <a href={`mailto:${value}`} target="_blank" rel="noopener noreferrer">
                                        {value}
                                    </a>
                                );
                            }
                            if (value.startsWith('http') || value.startsWith('www.')) {
                                return (
                                    <a href={value.startsWith('http') ? value : `http://${value}`} target="_blank" rel="noopener noreferrer">
                                        {value}
                                    </a>
                                );
                            }
                            return value;
                        }

                        return value !== undefined && value !== null ? String(value) : '';
                    }
                }

                return (
                    <div
                        style={{ position: "relative", display: "flex", alignItems: "center" }}
                        onMouseEnter={(e) => {
                            const editIcon = e.currentTarget?.querySelector?.(".edit-icon");
                            if (editIcon) {
                                editIcon.style.visibility = "visible";
                            }
                        }}
                        onMouseLeave={(e) => {
                            const editIcon = e.currentTarget?.querySelector?.(".edit-icon");
                            if (editIcon) {
                                editIcon.style.visibility = "hidden";
                            }
                        }}
                    >
                        {field.type === "boolean" ? (
                            <Checkbox checked={text} disabled />
                        ) : (
                            typeof text === 'object' && text !== null
                                ? JSON.stringify(text)
                                : (text ?? '')
                        )}

                        {["string", "integer", "boolean"].includes(field.type) &&
                            !field.is_auto_number &&
                            !field.formula &&
                            !security?.object_settings?.objectName?.object_setting?.edit
                                (
                                    <Tooltip title="Edit">
                                        <EditOutlined
                                            className="edit-icon"
                                            style={{
                                                marginLeft: 6,
                                                visibility: "hidden",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => handleInlineEditClick(record.key, field.name, record._id)}
                                        />
                                    </Tooltip>

                                )}
                    </div>
                );

            }
        }));

        // Calculate total width for proper horizontal scrolling
        const totalWidth = columns.reduce((sum, col) => sum + (col.width || 140), 0);
        const actionColumnWidth = !isGlobalSearch ? 110 : 0;
        const calculatedScrollWidth = totalWidth + actionColumnWidth;

        if (!isGlobalSearch) {

            columns.push({
                title: 'Action',
                key: 'operation',
                fixed: 'right',
                width: 110,
                render: (_, record) => (
                    <>
                        <Tooltip title="Edit">
                            <EditOutlined
                                onClick={() => handleEditClick(record)}
                                style={{ marginRight: 6, fontSize: '12px', cursor: 'pointer' }}
                            />
                        </Tooltip>

                        <Tooltip title="Clone">
                            <CopyOutlined
                                onClick={() => handleCloneClick(record)}
                                style={{ marginRight: 6, fontSize: '12px', cursor: 'pointer' }}
                            />
                        </Tooltip>

                        <Tooltip title="Delete">
                            <Popconfirm
                                title="Are you sure you want to delete this item?"
                                onConfirm={() => deleteRecord(record)}
                                okText="Yes"
                                cancelText="No"
                            >
                                <DeleteOutlined style={{ color: 'red', marginRight: 0, fontSize: '12px', cursor: 'pointer' }} />
                            </Popconfirm>
                        </Tooltip>
                    </>
                ),


            });
        }

        setColumns(columns);
        setScrollWidth(calculatedScrollWidth); // Set the calculated scroll width
    }

    return (
        <>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <Spin size="large" style={{ marginTop: '20px' }} />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={records}
                    scroll={{
                        x: scrollWidth,
                        y: 400
                    }}
                    tableLayout="fixed"
                    style={{ width: '100%' }}
                    size="small"
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        onChange: (page, size) => {
                            if (isGlobalSearch) {
                                fetchSingleObjectRecords(page, size);
                            } else {
                                fetchRecords(selectedListView, page, size);
                            }
                        },
                        onShowSizeChange: (current, size) => {
                            setPageSize(size);
                            if (isGlobalSearch) {
                                fetchSingleObjectRecords(current, size);
                            } else {
                                fetchRecords(selectedListView, current, size);
                            }
                        },
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`,
                    }}
                />
            )}

            {inlineEditing && (
                <Affix offsetBottom={0}>
                    <div
                        style={{
                            width: '90%',
                            padding: '6px 0',
                            background: 'rgba(240, 242, 245, 0.9)',
                            position: 'fixed',
                            bottom: '8px',
                            left: 110
                        }}
                    >
                        <Row justify="center">
                            <Button onClick={handleCancel} style={{ marginRight: 6 }} size="small">
                                Cancel
                            </Button>
                            <Button type="primary" onClick={handleSave} size="small">
                                Save
                            </Button>
                        </Row>
                    </div>
                </Affix>
            )}
        </>
    );
}

export default ListViewTable;